import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LOGO_ICON, NAV, SITE } from "@/lib/siteData";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Always solid on non-home pages
  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-site flex h-16 sm:h-20 items-center justify-between gap-4">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-3 group shrink-0">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border border-border group-hover:border-[hsl(var(--primary))] transition-colors overflow-hidden shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
            <img
              src={LOGO_ICON}
              alt="Pie in the Sky Productions fish icon"
              className="h-9 w-9 object-contain"
              loading="eager"
              decoding="async"
            />
          </span>
          <span className="font-display text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.18em] sm:tracking-[0.22em] text-foreground leading-tight max-w-[160px] sm:max-w-none">
            Pie in the Sky<br className="sm:hidden" /><span className="hidden sm:inline"> </span>Productions
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((item) => item.children ? (
            <DropdownMenu key={item.label}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card border-border w-72">
                <DropdownMenuItem asChild>
                  <Link data-testid={`nav-${item.label.toLowerCase()}-overview`} to={item.href} className="font-display uppercase tracking-[0.14em] text-[hsl(var(--primary))]">All {item.label.toLowerCase()} overview</Link>
                </DropdownMenuItem>
                {item.children.map((c) => (
                  <DropdownMenuItem key={c.href} asChild>
                    <Link data-testid={`nav-child-${c.href.split("/").pop()}`} to={c.href} className="text-foreground">{c.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <NavLink
              key={item.href}
              to={item.href}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) => `text-sm transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button data-testid="header-cta-button" asChild className="rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5 h-10">
            <Link to="/contact">Start a project</Link>
          </Button>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-toggle" aria-label="Open menu" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card/60">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent data-testid="mobile-nav-sheet" side="right" className="bg-background border-border w-[88%] max-w-sm p-0 overflow-y-auto">
              <div className="flex items-center justify-between px-5 h-16 border-b border-border">
                <SheetTitle className="font-display uppercase tracking-[0.18em] text-foreground text-base">Menu</SheetTitle>
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <nav className="flex flex-col px-5 py-6 gap-1" aria-label="Mobile">
                {NAV.map((item) => (
                  <div key={item.label} className="py-1">
                    <NavLink
                      to={item.href}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                      onClick={() => setOpen(false)}
                      className="font-display block text-3xl uppercase tracking-[0.04em] py-1 hover:text-[hsl(var(--primary))]"
                    >
                      {item.label}
                    </NavLink>
                    {item.children && (
                      <div className="mt-2 mb-3 pl-3 border-l border-border space-y-2">
                        {item.children.map((c) => (
                          <NavLink
                            key={c.href}
                            to={c.href}
                            onClick={() => setOpen(false)}
                            className="block text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Button data-testid="mobile-cta-button" asChild className="mt-6 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110">
                  <Link to="/contact" onClick={() => setOpen(false)}>Start a project</Link>
                </Button>
                <div className="mt-10 text-xs text-muted-foreground">
                  <p>{SITE.location}</p>
                  <p className="mt-1">{SITE.email}</p>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
